import { FeaturesType } from '@follow/cdk'
import { useMutation } from '@tanstack/react-query'
import { insiPatientSanityCheck } from '../../../misc/insi.utilities'
import { UserFriendlyError } from '../../../model/Error'
import { InsiIdentityCheckPayload, InsiPatient } from '../../../model/Insi'
import { addError, addValid, addWarning } from '../../../store/message'
import { useActionDispatch } from '../../utils'
import { useUserEnabledFeature } from '../../utils/user'
import { useInsiRestIdentityCheck } from './insiRest'
import { useInsiConsultIdentityCheck } from './insiConsult'
import { isDefined } from '../../../misc/functions.utilities'
import { InsCheckTypeValue, InsStatusCheckTypeValue } from '../../../features/ins/types/ins.types'
import { useClearINSIStatusHook } from '../../../components/patient/PatientForm/useClearINSIStatus.hook'
import InsUtilities from '../../../features/ins/utilities/Ins.utilities'
import { InsStatusCheckTypeEnums } from '../../../features/ins/enums/insStatusCheckType.enums'

interface UseInsiIdentityCheckVariables {
  patient: InsiIdentityCheckPayload | null
  type?: InsCheckTypeValue
}

export interface InsiIdentitySuccessResponse {
  patient?: InsiPatient | null
  codeCR?: InsStatusCheckTypeValue | null
  type?: InsCheckTypeValue
}

export interface InsiIdentityErrorResponse {
  error: Error
  type?: InsCheckTypeValue
  codeCR?: InsStatusCheckTypeValue
}

export const useInsiIdentityCheck = () => {
  const insiService = useUserEnabledFeature(FeaturesType.insiValidation)

  const dispatchValid = useActionDispatch(addValid)
  const dispatchWarning = useActionDispatch(addWarning)
  const dispatchError = useActionDispatch(addError)
  const dispatchResetINSIStatus = useClearINSIStatusHook()

  const { mutateAsync: insiRestCheck } = useInsiRestIdentityCheck()
  const { mutateAsync: insiConsultCheck } = useInsiConsultIdentityCheck()

  const mutation = useMutation<
    InsiIdentitySuccessResponse | null,
    InsiIdentityErrorResponse,
    UseInsiIdentityCheckVariables
  >({
    mutationFn: async ({ patient, type }) => {
      if (!insiService) {
        return null
      }
      // Identity check in compliance with the INSi service
      let patientResponse: InsiPatient | undefined = undefined
      let codeCR: InsStatusCheckTypeValue | undefined = undefined
      if (insiService === 'insiValidationRest') {
        if (!isDefined(patient)) {
          throw new UserFriendlyError(
            'error',
            "Impossible de vérifier l'identité du patient",
            'Informations incomplètes',
          )
        }
        try {
          patientResponse = await insiRestCheck({ patient })

        } catch (error) {
          console.error(error)
          // eslint-disable-next-line no-throw-literal
          throw {
            error,
            type,
          }
        }
      }
      if (insiService === 'insiValidationInApp') {
        try {
          const response = await insiConsultCheck({ patient, type })
          // When the user cancels the verification by closing the modal window
          if (response === null) {
            console.log({response})
            return null
          }
          if (response?.patient) {
            patientResponse = response?.patient
          }
          if (response?.codeCR) {
            codeCR = response?.codeCR
          }
        } catch (error) {
          console.error(error)
          // eslint-disable-next-line no-throw-literal
          throw {
            error,
            type,
          }
        }
      }

      if (!patientResponse && codeCR) {
        return {
          patient: null,
          codeCR,
          type,
        }
      }

      if (!patientResponse && !codeCR) {
        throw new Error('Empty ins response, it should never happen')
      }
      // Traitement de la réponse
      const sanityCheckErrors = insiPatientSanityCheck(patientResponse!)
      if (sanityCheckErrors.length > 0) {
        const fieldNames = sanityCheckErrors
          .map((field) => {
            switch (field) {
              case 'birthDate':
                return 'Date de naissance'
              case 'birthFirstName':
                return 'Prénom de naissance'
              case 'birthLastName':
                return 'Nom de naissance'
              case 'sex':
                return 'Sexe'
              default:
                return `Champ inconnu (${field})`
            }
          })
          .join(', ')

        const plural = fieldNames.length > 1 ? 's' : ''
        throw new UserFriendlyError(
          'error',
          'Le service INSi a retourné une identité contenant des erreurs.',
          `Information${plural} erronnée${plural} : ${fieldNames}`,
        )
      }

      return {
        patient: patientResponse || null,
        codeCR: codeCR,
        type,
      }
    },
    onSuccess: (data) => {
      if (data?.patient === null && data?.codeCR === null) return

      const statusCode = data?.patient?.codeCR || data?.codeCR || InsStatusCheckTypeEnums.KO
      const message = InsUtilities.getIdentityStatusMessage(statusCode)

      if (
        statusCode !== InsStatusCheckTypeEnums.KO &&
        statusCode === InsStatusCheckTypeEnums['00']
      ) {
        dispatchValid(message)
      } else if (statusCode === InsStatusCheckTypeEnums['01'] || statusCode === InsStatusCheckTypeEnums['02']) {
        dispatchWarning(message)
        dispatchResetINSIStatus()
      } else {
        throw new Error('Empty ins response, it should never happen')
      }
    },
    onError: (dataError) => {
      const { error } = dataError
      if (!(error instanceof UserFriendlyError)) {
        const message = InsUtilities.getIdentityStatusMessage(InsStatusCheckTypeEnums.KO)
        dispatchError(message)
        dispatchResetINSIStatus()
      }
    },
  })

  return mutation
}
